import { yupResolver } from '@hookform/resolvers/yup';
import { useMutation } from '@tanstack/react-query';
import { useNotifications } from '@toolpad/core';
import { useForm } from 'react-hook-form';
import { FormContainer, TextFieldElement } from 'react-hook-form-mui';
import { useNavigate } from 'react-router';
import { DeleteForever } from '@mui/icons-material';
import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
} from '@mui/material';
import { GridColDef, GridRowParams } from '@mui/x-data-grid';

import {
    DataGrid,
    DataGridCard,
    SubstitutionInput,
} from '@barboxx/admin-components';
import {
    CREATE_STANDARD_DRINK_SUBSTITUTION_SCHEMA,
    ID_WIDTH,
    NAME_MIN_WIDTH,
} from '@barboxx/admin-utils';
import Api from '@barboxx/api';
import { StandardDrink, StandardDrinkSubstitution } from '@barboxx/models';
import { useBool } from '@barboxx/react-utils';

const COLUMNS: GridColDef<StandardDrinkSubstitution>[] = [
    {
        field: 'id',
        headerName: 'Id',
        width: ID_WIDTH,
    },
    {
        field: 'name',
        headerName: 'Name',
        minWidth: NAME_MIN_WIDTH,
        flex: 1,
    },
    {
        field: 'description',
        headerName: 'Description',
        editable: true,
        minWidth: NAME_MIN_WIDTH,
        flex: 2,
    },
    {
        field: 'priority',
        headerName: 'Priority',
        minWidth: 100,
        align: 'right',
        headerAlign: 'right',
        editable: true,
    },
];

interface Props {
    standardDrink: StandardDrink.DetailsResponse;
    refetch: () => void;
}

export const SubstitutionsListCard = ({ standardDrink, refetch }: Props) => {
    const { id, substitutions } = standardDrink;
    const navigate = useNavigate();
    const notifications = useNotifications();

    const [open, dialogActions] = useBool();

    const formContext = useForm<StandardDrinkSubstitution.CreateRequest>({
        resolver: yupResolver(CREATE_STANDARD_DRINK_SUBSTITUTION_SCHEMA),
        defaultValues: {
            drinkId: id,
        },
    });

    const mutateCreateStandardDrinkSubstitution = useMutation({
        mutationKey: ['standardDrinkSubstitution'],
        mutationFn: (data: StandardDrinkSubstitution.CreateRequest) =>
            Api.StandardDrinks.Substitution.postStandardDrinkSubstitution(data),
    });

    const mutateDeleteSubstitution = useMutation({
        mutationKey: ['deleteStandardDrinkSubstitution'],
        mutationFn: (data: StandardDrinkSubstitution.DeleteRequest) =>
            Api.StandardDrinks.Substitution.deleteStandardDrinkSubstitution(
                data
            ),
    });

    const onSubmit = async (
        values: StandardDrinkSubstitution.CreateRequest
    ) => {
        return mutateCreateStandardDrinkSubstitution
            .mutateAsync(values)
            .then(({ data }) => {
                notifications.show(`Created  substitution!`, {
                    severity: 'success',
                });
                handleCancelCreate();
                refetch();
            })
            .catch(e => {
                notifications.show('Error creating substitution!', {
                    severity: 'error',
                });
            });
    };

    const handleCancelCreate = () => {
        formContext.reset();
        dialogActions.off();
    };

    const handleRowClick = (
        params: GridRowParams<StandardDrinkSubstitution>
    ) => {
        navigate(`/core/substitutions/${params.row.substitutionId}`);
    };

    const handleDeleteSubstitution = async (
        values: StandardDrinkSubstitution.DeleteRequest
    ) => {
        return mutateDeleteSubstitution
            .mutateAsync(values)
            .then(({ data }) => {
                notifications.show(`Deleted substitution!`, {
                    severity: 'success',
                });
                refetch();
            })
            .catch(e => {
                notifications.show('Error deleting substitution!', {
                    severity: 'error',
                });
            });
    };

    const columns = COLUMNS.concat([
        {
            field: 'actions',
            headerName: 'Actions',
            minWidth: NAME_MIN_WIDTH,
            type: 'actions',
            getActions: params => [
                // <Button
                //     key="edit"
                //     onClick={() => {
                //         navigate(`${params.id}`);
                //     }}
                // >
                //     Edit
                // </Button>,
                <IconButton
                    key="delete"
                    onClick={() => {
                        handleDeleteSubstitution(params.row);
                    }}
                >
                    <DeleteForever />
                </IconButton>,
            ],
        },
    ]);
    return (
        <>
            <DataGridCard
                title="Substitutions List"
                HeaderComponent={() => (
                    <Button variant="contained" onClick={dialogActions.on}>
                        Add Substitution
                    </Button>
                )}
            >
                <DataGrid
                    columns={columns}
                    disableColumnMenu
                    disableRowSelectionOnClick
                    paginationMode="client"
                    onRowClick={handleRowClick}
                    rows={substitutions}
                />
            </DataGridCard>
            <Dialog
                open={open}
                onClose={dialogActions.off}
                aria-labelledby="alert-dialog-title"
                aria-describedby="alert-dialog-description"
                fullWidth
            >
                <DialogTitle id="alert-dialog-title">
                    Create Substitution
                </DialogTitle>
                <FormContainer onSuccess={onSubmit} formContext={formContext}>
                    <DialogContent
                        sx={{
                            gap: 2,
                            display: 'flex',
                            flexDirection: 'column',
                        }}
                    >
                        <SubstitutionInput />
                        <TextFieldElement
                            label="Priority"
                            name="priority"
                            fullWidth
                        />
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={handleCancelCreate} color="inherit">
                            Cancel
                        </Button>

                        <Button
                            color="primary"
                            variant="contained"
                            loading={formContext.formState.isSubmitting}
                            type="submit"
                            disabled={formContext.formState.isSubmitting}
                        >
                            Create
                        </Button>
                    </DialogActions>
                </FormContainer>
            </Dialog>
        </>
    );
};
